// src/components/modals/CreateStatusModal.tsx
import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';

interface CreateStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStatusCreated?: (status: { name: string; color: string }) => void;
}

const colorOptions = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#6B7280', '#84CC16', '#06B6D4', '#F97316',
  '#6366F1', '#D946EF', '#0EA5E9', '#64748B', '#14B8A6'
];

export const CreateStatusModal: React.FC<CreateStatusModalProps> = ({
  isOpen,
  onClose,
  onStatusCreated
}) => {
  const [statusName, setStatusName] = useState('');
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusName.trim()) return;

    // Here you would typically save the status to your backend
    if (onStatusCreated) {
      onStatusCreated({
        name: statusName.trim(),
        color: selectedColor
      });
    }

    // Reset form and close modal
    setStatusName('');
    setSelectedColor(colorOptions[0]);
    onClose();
  };

  const handleClose = () => {
    setStatusName('');
    setSelectedColor(colorOptions[0]);
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="Create Status"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-6 px-4">
        {/* Status Name Input */}
        <div>
          <Input
            label="Status Name"
            placeholder="Enter status name"
            value={statusName}
            onChange={(e) => setStatusName(e.target.value)}
            required
            autoFocus
          />
        </div>

        {/* Color Selection */}
        <div>
          <div className="mb-3">
            <h3 className="text-sm font-medium text-gray-900">Color</h3>
          </div>
          
          {/* Color Options */}
          <div className="flex flex-wrap gap-2">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                className={`w-8 h-8 rounded-full transition-all
                     ${
                  selectedColor === color 
                    ? 'border-gray-900 scale-110' 
                    : 'border-gray-300 hover:border-gray-400'
                }
                    `}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedColor(color)}
              />
            ))}
          </div>
        </div>

        {/* Save Button */}
        <Button 
          type="submit" 
          variant="primary" 
          className="w-full"
          disabled={!statusName.trim()}
        >
          Save Status
        </Button>
      </form>
    </Modal>
  );
};

export default CreateStatusModal;